import { useCallback, useEffect, useRef } from 'react';

const ACTIVITY_CHECK_INTERVAL = 1000; // Check for activity every second
export const ACTIVITY_STORAGE_KEY = 'app_last_activity_time';
export const IDLE_STATE_KEY = 'app_idle_state';

export function useCustomIdleTimer(timeout: number, onIdle: () => void, enabled: boolean) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const updateActivity = useCallback(() => {
    if (!enabledRef.current) return;

    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
    localStorage.setItem(IDLE_STATE_KEY, 'active');
  }, []);

  const checkIdleState = useCallback(() => {
    if (!enabledRef.current) return;

    const storedActivity = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    const lastActivity = storedActivity ? parseInt(storedActivity, 10) : lastActivityRef.current;
    const now = Date.now();
    const timeSinceActivity = now - lastActivity;

    if (timeSinceActivity >= timeout) {
      const idleState = localStorage.getItem(IDLE_STATE_KEY);
      if (idleState !== 'idle') {
        localStorage.setItem(IDLE_STATE_KEY, 'idle');
        onIdle();
      }
    }
  }, [timeout, onIdle]);

  const startTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!enabledRef.current) return;

    // Set initial activity
    updateActivity();

    // Check for idle state periodically, we optimize this next time, for now we just use a simple interval
    intervalRef.current = setInterval(checkIdleState, ACTIVITY_CHECK_INTERVAL);
  }, [updateActivity, checkIdleState]);

  const stopTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  useEffect(() => {
    console.log(enabled);
    if (!enabled) {
      stopTimer();
      return;
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'contextmenu',
      'dblclick',
      'mouseup',
      'pointerdown',
      'pointerup',
      'touchend',
      'focus',
      'blur',
    ];

    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledUpdateActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        updateActivity();
        throttleTimer = null;
      }, 500);
    };

    events.forEach((event) => {
      document.addEventListener(event, throttledUpdateActivity, { passive: true, capture: true });
    });

    // Activity in another tab arrives as a storage event, keeping all tabs in sync.
    // Listen for storage changes especiallu activity from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === ACTIVITY_STORAGE_KEY && event.newValue) {
        const newActivity = parseInt(event.newValue, 10);
        if (newActivity > lastActivityRef.current) {
          lastActivityRef.current = newActivity;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    startTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledUpdateActivity, { capture: true });
      });
      window.removeEventListener('storage', handleStorageChange);
      stopTimer();
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [enabled, startTimer, stopTimer, updateActivity]);

  return {
    reset: resetTimer,
    start: startTimer,
    stop: stopTimer,
  };
}
